import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface PaymentWebviewModalProps {
  url: string;
  onClose: () => void;
  onPaymentDone?: () => void;
}

const PaymentWebviewModal: React.FC<PaymentWebviewModalProps> = ({ url, onClose, onPaymentDone }) => {
  // Listen for postMessage from Mosan callback iframe
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'mosan_payment_done') {
        onClose();
        onPaymentDone?.();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onClose, onPaymentDone]);

  // Lock body scroll while modal is open so the page underneath doesn't
  // scroll behind the iframe (also avoids the mobile back bar peeking
  // through if any ancestor accidentally creates a stacking context).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Render via portal to <body> so the modal is detached from any ancestor
  // stacking context (e.g. AppShell wrapping pt-11). Z-50 then reliably
  // sits above the MobileBackBar (z-40).
  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-[420px] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" style={{ height: 'calc(100% - 2rem)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-sm font-semibold text-gray-900">Payment</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        {/* Iframe */}
        <iframe
          src={url}
          className="flex-1 w-full border-0"
          title="Mosan Payment"
        />
      </div>
    </div>,
    document.body,
  );
};

export default PaymentWebviewModal;
